import { useState, useCallback, useEffect } from 'react';
import { firstValueFrom } from 'rxjs';
import { pipelineService } from '../../../../services/pipelineService';
import { dispatchCustomEvent, listener } from '../../../../utils/dispatch';
import { matchIdentifications } from '../utils/xml-parser';
import { matchProducts } from '../../../../usecases/products-usecase';
import { createPdfBlob, createPdfUrl } from '../../../pages/invoice/controller';

export default function usePipelineReview({ companyId, profile, lookups, onApproved }) {
  const { products, thirds, spendTypes, municipalities, getClient } = lookups;

  const [activeDraft, setActiveDraft] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewPayload, setReviewPayload] = useState({});
  const [selectedThird, _setSelectedThird] = useState(null);
  const [userClearedThird, setUserClearedThird] = useState(false);

  const setSelectedThird = useCallback((val) => {
    _setSelectedThird(val);
    setUserClearedThird(true);
  }, []);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedFormPay, setSelectedFormPay] = useState('1');
  const [originalReviewPayload, setOriginalReviewPayload] = useState(null);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [approving, setApproving] = useState(false);
  const [loadingReviewStep, setLoadingReviewStep] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [voucherCandidates, setVoucherCandidates] = useState([]);

  // PDF URL generation
  useEffect(() => {
    if (reviewPayload.file_base64 && reviewPayload.file_mime === 'application/pdf') {
      const blob = createPdfBlob(reviewPayload.file_base64);
      const url = createPdfUrl(blob);
      setPdfUrl(url);
      return () => { setTimeout(() => URL.revokeObjectURL(url), 5000); };
    } else {
      setPdfUrl(null);
    }
  }, [reviewPayload.file_base64, reviewPayload.file_mime]);

  // Listen for third party selection events
  useEffect(() => {
    const clean = listener('btn-thirds-add', (e) => {
      const third = e.detail?.data;
      if (third) {
        setSelectedThird(third);
        setReviewPayload(prev => ({
          ...prev,
          tercero: { ...prev.tercero, id: third.id }
        }));
      }
    });
    return () => clean();
  }, []);

  // Auto-match third party when thirds list reloads
  useEffect(() => {
    if (userClearedThird) return;
    if (!activeDraft || !thirds.length) return;
    if (selectedThird && !selectedThird.isNew) return;
    const docTercero = activeDraft.extracted_data?.tercero;
    if (!docTercero) return;
    const { payloadThirdId, selectedThirdObj } = resolveThirdState(docTercero, thirds);
    if (selectedThirdObj && selectedThird?.id !== selectedThirdObj.id) {
      _setSelectedThird(selectedThirdObj);
      setReviewPayload(prev => ({
        ...prev,
        tercero: { ...prev.tercero, id: payloadThirdId }
      }));
    }
  }, [thirds, activeDraft, selectedThird, userClearedThird]);

  const resolveThirdState = useCallback((docTercero, currentThirds = thirds, currentMunis = municipalities) => {
    if (!docTercero) return { payloadThirdId: '', selectedThirdObj: null };
    const matchedThird = currentThirds.find(t => matchIdentifications(t.identification, docTercero.identification));
    if (matchedThird) {
      return { payloadThirdId: matchedThird.id, selectedThirdObj: { ...matchedThird, isNew: false } };
    }
    let municipalityId = null;
    let postalZoneCode = docTercero.postal_code || '';
    if (currentMunis && currentMunis.length > 0) {
      let matchedMuni = currentMunis.find(m => String(m.code) === String(docTercero.postal_code));
      if (!matchedMuni && docTercero.municipality) {
        const normExtracted = String(docTercero.municipality).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        matchedMuni = currentMunis.find(m => {
          const normMuni = String(m.name).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          return normMuni.includes(normExtracted) || normExtracted.includes(normMuni);
        });
      }
      if (matchedMuni) {
        municipalityId = matchedMuni.id;
        if (!postalZoneCode) postalZoneCode = matchedMuni.code || '';
      }
    }
    return {
      payloadThirdId: 'new',
      selectedThirdObj: {
        id: 'new', name: docTercero.name || 'Tercero Nuevo',
        identification: docTercero.identification || '',
        email: docTercero.email || '', phone: docTercero.phone || '',
        address: docTercero.address || '',
        municipalityId, postalZoneCode, isNew: true
      }
    };
  }, [thirds, municipalities]);

  const handleOpenReview = async (draft) => {
    setUserClearedThird(false);
    setLoadingReviewStep('Obteniendo información completa del borrador...');
    try {
      const client = getClient();
      const res = await firstValueFrom(client.get(`pipeline/drafts/${draft.id}`));
      const fullDraft = res?.data?.data || res?.data || draft;
      setActiveDraft(fullDraft);
      const data = JSON.parse(JSON.stringify(fullDraft.extracted_data));

      if (draft.doc_type === 'bank_statement' && fullDraft.suggested_accounting?.reconciliation) {
        data._reconciliation = fullDraft.suggested_accounting.reconciliation;
      }

      setLoadingReviewStep('Verificando y asociando proveedor...');
      let { payloadThirdId, selectedThirdObj } = resolveThirdState(data.tercero);

      if (selectedThirdObj?.isNew && data.tercero?.identification) {
        try {
          const searchRes = await firstValueFrom(client.get(`thirds?page=1&limit=10&query=${encodeURIComponent(data.tercero.identification)}`));
          const foundThirds = searchRes?.data?.data || searchRes?.data || [];
          const existing = foundThirds.find(t => matchIdentifications(t.identification, data.tercero.identification));
          if (existing) {
            payloadThirdId = existing.id;
            selectedThirdObj = { ...existing, isNew: false };
          }
        } catch (e) {
          console.warn("Failed to check existing third", e);
        }
      }
      if (data.tercero) data.tercero.id = payloadThirdId;
      _setSelectedThird(selectedThirdObj);

      let initialMethods = [];
      let initialFormPay = '1';

      if (draft.doc_type === 'buy') {
        setLoadingReviewStep('Buscando coincidencias de productos...');
        const matchPayload = (data.items || []).map((item, idx) => ({
          idx: String(idx), description: item.name || '', sku: item.sku || ''
        }));
        let matches = [];
        try {
          const uid = localStorage.getItem('user_uid') || '';
          const cid = companyId ? String(companyId) : (localStorage.getItem('cid') || null);
          matches = await matchProducts(uid, cid, matchPayload);
        } catch (e) {
          console.warn("Failed to match products", e);
        }
        data.items = (data.items || []).map((item, idx) => {
          let matchedProd = null;
          let matchStatus = 'pending';
          if (item.product_id) {
            matchedProd = products.find(p => p.id === Number(item.product_id));
            if (matchedProd) matchStatus = item.matchStatus || 'manual';
          }
          if (!matchedProd && matches?.length > 0) {
            const match = matches.find(m => m.idx === String(idx));
            if (match?.related_item) {
              matchedProd = products.find(p => p.id === Number(match.related_item.id));
              if (matchedProd) matchStatus = match.match_status;
            }
          }
          if (!matchedProd) {
            const bySku = products.find(p => p.sku === item.sku);
            const byName = !bySku ? products.find(p => p.name?.toLowerCase().includes(item.name?.toLowerCase())) : null;
            matchedProd = bySku || byName;
            if (bySku) matchStatus = 'auto';
            else if (byName) matchStatus = 'suggested';
          }
          return { ...item, product_id: matchedProd?.id || '', id: matchedProd?.id || '', relatedItem: matchedProd || null, matchStatus };
        });
        if (data.selectedMethods && Array.isArray(data.selectedMethods) && data.selectedMethods.length > 0) {
          initialMethods = data.selectedMethods.map(m => Number(m.id) > 100 ? { ...m, id: 1, method: 'Tarjeta Débito' } : m);
        } else {
          initialMethods = [{ id: 1, method: 'Tarjeta Débito', value: data.total }];
        }
        initialFormPay = data.selectedFormPay || '1';
        setSelectedMethods(initialMethods);
        setSelectedFormPay(initialFormPay);
      } else if (draft.doc_type === 'spend') {
        setLoadingReviewStep('Sincronizando tipos de gasto...');
        let currentSpendTypes = spendTypes;
        try {
          const response = await firstValueFrom(client.get('spends/spend_type?page=1&limit=1000'));
          const refreshed = response?.data?.data || response?.data || [];
          lookups.setSpendTypes(refreshed);
          currentSpendTypes = refreshed;
        } catch (e) { console.warn("Failed to refresh spend types", e); }

        data.service_lines = (data.service_lines || []).map((line, idx) => {
          let spend_type_id = line.spend_type_id || '';
          if (!spend_type_id && line.matched_spend_type_id) {
            const exists = currentSpendTypes.find(st => st.id === line.matched_spend_type_id);
            if (exists) spend_type_id = exists.id;
          }
          if (!spend_type_id) {
            const desc = (line.description || '').toLowerCase();
            const matched = currentSpendTypes.find(st => st.name?.toLowerCase() === desc)
              || currentSpendTypes.find(st => desc.split(' ').some(word => word.length > 3 && st.name?.toLowerCase().includes(word)));
            spend_type_id = matched ? matched.id : `new_${idx}`;
          }
          const lineAmount = (line.base || line.tax) ? (Number(line.base || 0) + Number(line.tax || 0)) : (line.amount || 0);
          let suggestedSpendTypeData;
          if (String(spend_type_id).startsWith('new_') && line.suggested_new_spend_type) {
            suggestedSpendTypeData = { ...line.suggested_new_spend_type, id: spend_type_id, isNew: true };
          }
          return { ...line, id: line.id || idx + 1, spend_type_id, spend_type_data: suggestedSpendTypeData || line.spend_type_data, amount: lineAmount };
        });
        initialMethods = (data.selectedMethods && Array.isArray(data.selectedMethods) && data.selectedMethods.length > 0)
          ? data.selectedMethods : [{ id: 1, method: 'Tarjeta Débito', value: data.total }];
        setSelectedMethods(initialMethods);
      }

      // Load voucher candidates for bank statement
      if (draft.doc_type === 'bank_statement') {
        setLoadingReviewStep('Cargando comprobantes para conciliación...');
        try {
          const vouchersRes = await firstValueFrom(client.get('accounting/vouchers?page=1&limit=200'));
          let vouchersList = [];
          if (Array.isArray(vouchersRes)) {
            vouchersList = vouchersRes;
          } else if (Array.isArray(vouchersRes?.data?.data)) {
            vouchersList = vouchersRes.data.data;
          } else if (Array.isArray(vouchersRes?.data)) {
            vouchersList = vouchersRes.data;
          }
          setVoucherCandidates(vouchersList.length > 0
            ? vouchersList.map(v => ({ id: v.id, date: v.date || '', account_code: '', account_name: '', description: v.descripcion || v.concept || `Comprobante #${v.id}`, debit: Number(v.total_debit || v.total || 0), credit: Number(v.total_credit || 0) }))
            : []);
        } catch (e) {
          console.warn('Failed to load voucher candidates', e);
          setVoucherCandidates([]);
        }

        // Pre-popular suggested_entry para movimientos sin asignación
        const txs = data.transactions || data.movements || [];
        const bankAccountId = data.bank_account_id;
        if (txs.length > 0) {
          txs.forEach((tx, i) => {
            if (!tx.suggested_entry) {
              txs[i] = { ...tx, matched_to_type: tx.matched_to_type || 'unmatched', suggested_entry: generateDefaultEntryForMovement(tx, bankAccountId) };
            }
          });
          data.transactions = txs;
          data.movements = txs;
        }
      }

      const snapshot = {
        documento_compra: data.documento_compra || '',
        terceroId: payloadThirdId,
        items: draft.doc_type === 'buy' ? (data.items || []).map(item => ({ product_id: item.product_id || '' })) : [],
        service_lines: draft.doc_type === 'spend' ? (data.service_lines || []).map(line => ({ spend_type_id: line.spend_type_id || '', amount: Number(line.amount || 0) })) : [],
        methods: initialMethods.map(m => ({ id: m.id, value: Number(m.value) })),
        formPay: draft.doc_type === 'buy' ? initialFormPay : null
      };
      setOriginalReviewPayload(snapshot);
      setReviewPayload(data);
      setShowReviewModal(true);
      setTimeout(() => dispatchCustomEvent('modalreview-modal', { eventKey: 'review-modal' }), 50);
    } catch (err) {
      console.error("Error loading review data", err);
      dispatchCustomEvent('popup', { message: 'Error al abrir el borrador.', type: 'error' });
    } finally {
      setLoadingReviewStep('');
      dispatchCustomEvent('loading-btn-review');
    }
  };

  const handleServiceLinesChange = (newLines) => {
    const mappedLines = newLines.map(line => {
      const spendType = line.spend_type_data || {};
      const amount = Math.round(line.amount || 0);
      let tax = line.tax || 0;
      let base = line.base || amount;
      let retention = line.retention || 0;
      if (spendType.id && !String(spendType.id).startsWith('new_')) {
        const taxRate = ('tax' in spendType && (spendType.tax === null || spendType.tax.disabled)) ? 0 : (spendType.tax?.value || 0);
        const retRate = ('retention' in spendType && (spendType.retention === null || spendType.retention.disabled)) ? 0 : (spendType.retention?.value || 0);
        tax = Math.round(amount - (amount / (1 + taxRate)));
        base = amount - tax;
        retention = Math.round(base * retRate);
      }
      return { ...line, spend_type_id: spendType.id, amount, base, tax, retention };
    });
    const netTotal = mappedLines.reduce((sum, l) => sum + l.amount - (l.retention || 0), 0);
    setReviewPayload(prev => ({ ...prev, service_lines: mappedLines, total: netTotal }));
  };

  const handlePayMethodChange = (methods) => setSelectedMethods([...methods]);

  const handleArrayRelaterChange = useCallback((updatedRelaterItems) => {
    if (!updatedRelaterItems?.length) return;
    const updatedItems = updatedRelaterItems.map(item => ({
      ...item,
      name: item.description,
      amount: Number(item.quantity),
      buy_value: Number(item.unit_price || item.price),
      total: Number(item.total),
      sku: item.sku,
      product_id: item.relatedItem?.id || '',
      id: item.relatedItem?.id || '',
      relatedItem: item.relatedItem || null,
      matchStatus: item.matchStatus || 'pending'
    }));
    setReviewPayload(prev => ({ ...prev, items: updatedItems }));
  }, []);

  const hasUnsavedChanges = () => {
    if (!originalReviewPayload || !reviewPayload || !activeDraft) return false;
    if ((reviewPayload.documento_compra || '') !== (originalReviewPayload.documento_compra || '')) return true;
    if (String(selectedThird?.id || '') !== String(originalReviewPayload.terceroId || '')) return true;
    if (activeDraft.doc_type === 'buy' && String(selectedFormPay) !== String(originalReviewPayload.formPay)) return true;
    if (selectedMethods.length !== originalReviewPayload.methods.length) return true;
    for (let i = 0; i < selectedMethods.length; i++) {
      const c = selectedMethods[i], o = originalReviewPayload.methods[i];
      if (!o || String(c.id) !== String(o.id) || Math.round(Number(c.value)) !== Math.round(Number(o.value))) return true;
    }
    if (activeDraft.doc_type === 'buy') {
      const ci = reviewPayload.items || [], oi = originalReviewPayload.items || [];
      if (ci.length !== oi.length) return true;
      for (let i = 0; i < ci.length; i++) {
        if (!oi[i] || String(ci[i].product_id || '') !== String(oi[i].product_id || '')) return true;
      }
    }
    if (activeDraft.doc_type === 'spend') {
      const cl = reviewPayload.service_lines || [], ol = originalReviewPayload.service_lines || [];
      if (cl.length !== ol.length) return true;
      for (let i = 0; i < cl.length; i++) {
        if (!ol[i]) return true;
        if (String(cl[i].spend_type_id || '') !== String(ol[i].spend_type_id || '')) return true;
        if (Math.round(Number(cl[i].amount || 0)) !== Math.round(Number(ol[i].amount || 0))) return true;
      }
    }
    return false;
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) { setShowExitWarning(true); return false; }
    setShowReviewModal(false);
    setOriginalReviewPayload(null);
    setActiveDraft(null);
    dispatchCustomEvent('modal-close-review-modal', {});
  };

  const handleSaveDraftChanges = async () => {
    setSavingDraft(true);
    try {
      const updatedExtractedData = {
        ...(reviewPayload || {}),
        tercero: { ...((reviewPayload && reviewPayload.tercero) || {}), id: selectedThird?.id || '' },
        selectedMethods,
        selectedFormPay: activeDraft.doc_type === 'buy' ? selectedFormPay : null
      };
      await pipelineService.updateDraft(activeDraft.id, {
        document_name: activeDraft.document_name,
        extracted_data: updatedExtractedData,
        suggested_accounting: activeDraft.suggested_accounting
      }, companyId);
      dispatchCustomEvent('popup', { message: 'Cambios del borrador guardados correctamente.', type: 'success' });
      setShowExitWarning(false);
      setOriginalReviewPayload(null);
      setActiveDraft(null);
      setShowReviewModal(false);
      if (onApproved) onApproved();
    } catch (e) {
      console.error(e);
      dispatchCustomEvent('popup', { message: e.response?.data?.message || 'Error al guardar los cambios.', type: 'error' });
    } finally { setSavingDraft(false); }
  };

  const handleDiscardLocalChanges = () => {
    setShowExitWarning(false);
    setOriginalReviewPayload(null);
    setActiveDraft(null);
    setShowReviewModal(false);
  };

  const handleApproveSubmit = async () => {
    setApproving(true);
    try {
      const payload = { ...reviewPayload };

      if (activeDraft.doc_type === 'bank_statement') {
        await pipelineService.approveDraft(activeDraft.id, payload, companyId);
        dispatchCustomEvent('popup', { message: 'Extracto Bancario procesado y conciliado con éxito.', type: 'success' });
        setShowReviewModal(false);
        setActiveDraft(null);
        if (onApproved) onApproved();
        setApproving(false);
        return;
      }

      if (selectedThird?.isNew && payload.tercero) {
        delete payload.tercero.id;
        if (activeDraft.doc_type === 'spend') delete payload.third_id;
      }

      if (activeDraft.doc_type === 'buy') {
        if (payload.retention === 0 || !payload.retention) payload.retention = null;
        payload.items = payload.items.map(item => ({
          ...item, id: Number(item.product_id), buy_value: Number(item.buy_value),
          amount: Number(item.amount), total: Number(item.total)
        }));
        payload.methods = selectedMethods.map(m => ({
          id: Number(m.id) > 100 ? 3 : Number(m.id), value: Number(m.value),
          payment_form_id: String(selectedFormPay || '1')
        }));
      }

      if (activeDraft.doc_type === 'spend') {
        const updatedServiceLines = [];
        for (const line of payload.service_lines) {
          let spendTypeId = line.spend_type_id;
          let suggestedNewSpendType = line.suggested_new_spend_type;
          let finalSpendTypeData = line.spend_type_data || {};
          if (String(spendTypeId).startsWith('new_')) {
            spendTypeId = null;
            if (finalSpendTypeData?.name && suggestedNewSpendType) {
              suggestedNewSpendType.name = finalSpendTypeData.name;
            } else if (finalSpendTypeData?.name && !suggestedNewSpendType) {
              suggestedNewSpendType = { name: finalSpendTypeData.name, is_recurrent: false, is_active: true };
            }
          } else {
            finalSpendTypeData = spendTypes.find(st => st.id === Number(spendTypeId)) || line.spend_type_data || {};
            spendTypeId = Number(spendTypeId);
            suggestedNewSpendType = undefined;
          }
          updatedServiceLines.push({
            ...line, spend_type_id: spendTypeId, spend_type_data: finalSpendTypeData,
            suggested_new_spend_type: suggestedNewSpendType,
            amount: Number(line.amount || 1), base: Number(line.base), tax: Number(line.tax || 0),
            retention: Number(line.retention || 0)
          });
        }
        payload.service_lines = updatedServiceLines;
        payload.tax = updatedServiceLines.reduce((acc, l) => acc + (Number(l.tax) || 0), 0);
        const finalNetTotal = updatedServiceLines.reduce((sum, l) => sum + Number(l.amount) - (Number(l.retention) || 0), 0);
        payload.value = finalNetTotal;
        payload.amount = finalNetTotal;
        payload.total = finalNetTotal;
        if (selectedMethods.length === 1) {
          payload.payment_methods = [{ id: Number(selectedMethods[0].id), value: finalNetTotal }];
        } else if (selectedMethods.length > 1) {
          const sumOthers = selectedMethods.slice(0, -1).reduce((sum, m) => sum + Number(m.value), 0);
          payload.payment_methods = [
            ...selectedMethods.slice(0, -1).map(m => ({ id: Number(m.id), value: Number(m.value) })),
            { id: Number(selectedMethods[selectedMethods.length - 1].id), value: Math.max(0, finalNetTotal - sumOthers) }
          ];
        } else {
          payload.payment_methods = [{ id: 1, value: finalNetTotal }];
        }
      }

      // Preservar o construir xml_invoice_data para eventos RADIAN
      if (!payload.xml_invoice_data) {
        if (payload.cufe || payload.xml_cufe || activeDraft.extracted_data?.cufe || activeDraft.extracted_data?.xml_cufe) {
          payload.xml_invoice_data = {
            cufe: payload.cufe || payload.xml_cufe || activeDraft.extracted_data?.cufe || activeDraft.extracted_data?.xml_cufe || null,
            invoice_number: payload.documento_compra || activeDraft.extracted_data?.documento_compra || null,
            supplier_nit: payload.tercero?.identification || selectedThird?.identification || activeDraft.extracted_data?.tercero?.identification || null,
            supplier_name: payload.tercero?.name || selectedThird?.name || activeDraft.extracted_data?.tercero?.name || null,
            supplier_email: payload.tercero?.email || selectedThird?.email || activeDraft.extracted_data?.tercero?.email || null,
          };
        } else if (activeDraft.extracted_data?.xml_invoice_data) {
          payload.xml_invoice_data = activeDraft.extracted_data.xml_invoice_data;
        }
      }

      await pipelineService.approveDraft(activeDraft.id, payload, companyId);
      dispatchCustomEvent('popup', {
        message: `${activeDraft.doc_type === 'buy' ? 'Compra' : 'Gasto'} registrado y borrador eliminado con éxito.`,
        type: 'success'
      });
      setShowReviewModal(false);
      setActiveDraft(null);
      if (onApproved) onApproved();
    } catch (e) {
      console.error(e);
      dispatchCustomEvent('popup', { message: e.response?.data?.message || 'Error al aprobar el documento.', type: 'error' });
    } finally { setApproving(false); }
  };

  const handleBankAccountChange = (newBankAccountId) => {
    const sub = lookups.subaccounts.find(s => String(s.id) === String(newBankAccountId));
    if (!sub) return;
    const code = sub.base_account?.account || '';
    const fullCode = `${code}${String(sub.code ?? '').padStart(2, '0')}`;
    const name = `${fullCode} - ${sub.description}`;
    setReviewPayload(prev => {
      const txs = prev.transactions || prev.movements || [];
      const updatedTxs = txs.map(tx => {
        if (tx.matched_to_type === 'unmatched' && tx.suggested_entry?.lines) {
          return {
            ...tx,
            suggested_entry: {
              ...tx.suggested_entry,
              lines: tx.suggested_entry.lines.map(line =>
                line.account_code?.startsWith('1110') || line.account_name?.toLowerCase().includes('banco')
                  ? { ...line, account_id: sub.id, account_code: fullCode, account_name: name }
                  : line
              )
            }
          };
        }
        return tx;
      });
      return { ...prev, bank_account_id: newBankAccountId, transactions: updatedTxs, movements: updatedTxs };
    });
  };

  const generateDefaultEntryForMovement = (movement, bankAccountId) => {
    const amt = Math.abs(Number(movement.amount || 0));
    const isWithdrawal = movement.type === 'withdrawal' || Number(movement.amount) < 0;
    const bankSub = lookups.subaccounts.find(s => String(s.id) === String(bankAccountId));
    const bankCode = bankSub ? `${bankSub.base_account?.account || ''}${String(bankSub.code ?? '').padStart(2, '0')}` : '111005';
    const bankName = bankSub ? `${bankCode} - ${bankSub.description}` : '111005 - Bancos';
    const counterpartPuc = movement.suggested_puc || (isWithdrawal ? '530515' : '4135');
    const counterpartSub = lookups.subaccounts.find(s => (s.base_account?.account || '').startsWith(counterpartPuc));
    const counterpartId = counterpartSub?.id || movement.suggested_account_id || null;
    const counterpartName = counterpartSub ? `${counterpartSub.base_account?.account || ''}${String(counterpartSub.code ?? '').padStart(2, '0')} - ${counterpartSub.description}` : (movement.suggested_account || `PUC ${counterpartPuc}`);
    const lines = isWithdrawal
      ? [{ account_id: counterpartId, account_code: counterpartPuc, account_name: counterpartName, debit: amt, credit: 0 },
         { account_id: bankSub?.id || null, account_code: bankCode, account_name: bankName, debit: 0, credit: amt }]
      : [{ account_id: bankSub?.id || null, account_code: bankCode, account_name: bankName, debit: amt, credit: 0 },
         { account_id: counterpartId, account_code: counterpartPuc, account_name: counterpartName, debit: 0, credit: amt }];
    return { lines };
  };

  const reset = () => {
    setActiveDraft(null);
    setShowReviewModal(false);
    setReviewPayload({});
    _setSelectedThird(null);
    setUserClearedThird(false);
    setSelectedMethods([]);
    setSelectedFormPay('1');
    setOriginalReviewPayload(null);
    setShowExitWarning(false);
    setSavingDraft(false);
    setApproving(false);
    setLoadingReviewStep('');
    setPdfUrl(null);
    setVoucherCandidates([]);
  };

  return {
    activeDraft, showReviewModal, reviewPayload, selectedThird, selectedMethods, selectedFormPay,
    loadingReviewStep, approving, savingDraft, showExitWarning, pdfUrl, voucherCandidates,
    setReviewPayload, setSelectedMethods, setSelectedThird, setShowExitWarning, setShowReviewModal,
    handleOpenReview, handleCloseAttempt, handleSaveDraftChanges, handleDiscardLocalChanges,
    handleApproveSubmit, handleServiceLinesChange, handlePayMethodChange, handleArrayRelaterChange,
    handleBankAccountChange, generateDefaultEntryForMovement, hasUnsavedChanges, reset
  };
}
